import { alpha, Button, Menu, MenuItem, MenuProps, styled } from '@mui/material'
import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import Divider from '@mui/material/Divider'
import ArchiveIcon from '@mui/icons-material/Archive'
import { VideoFile, Image, RequestQuote } from '@mui/icons-material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTranslation } from 'react-i18next'
import { Report } from './Report'

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}))

interface Props {
  invoiceCallBack: (report: Report, handleCloseMenuCallBack: () => void) => void
  editCallBack: (report: Report, handleCloseMenuCallBack: () => void) => void
  archiveCallBack: (report: Report, handleCloseMenuCallBack: () => void) => void
  downloadVideoCallBack: (report: Report, handleCloseMenuCallBack: () => void) => void
  downloadImageCallBack: (report: Report, handleCloseMenuCallBack: () => void) => void
  enableDownloadImage: boolean
  report: Report
}

export default function ReportActionMenu({
  invoiceCallBack,
  editCallBack,
  archiveCallBack,
  downloadVideoCallBack,
  downloadImageCallBack,
  enableDownloadImage,
  report,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const { t } = useTranslation()
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div>
      <Button
        id="demo-customized-button"
        aria-controls={open ? 'demo-customized-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {t('detectionReports.actionTitle')}
      </Button>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': 'demo-customized-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => invoiceCallBack(report, handleClose)} disableRipple>
          <RequestQuote />
          {t('detectionReports.invoiceRequest')}
        </MenuItem>
        <MenuItem onClick={() => archiveCallBack(report, handleClose)} disableRipple>
          <ArchiveIcon />
          {t('detectionReports.archiveReport')}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => editCallBack(report, handleClose)} disableRipple>
          <EditIcon />
          {t('detectionReports.editReport')}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => downloadVideoCallBack(report, handleClose)} disableRipple>
          <VideoFile />
          {t('detectionReports.downloadVideo')}
        </MenuItem>
        {enableDownloadImage && downloadImageCallBack && (
          <MenuItem onClick={() => downloadImageCallBack(report, handleClose)} disableRipple>
            <Image />
            {t('detectionReports.downloadImage')}
          </MenuItem>
        )}
      </StyledMenu>
    </div>
  )
}
